import Table from './Table';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {createStructuredSelector} from 'reselect';
import {
    getCommoditiesToggle,
    getCurrenciesToggle,
    getIndicators,
    getLocalToggle,
    getOffshoreToggle
} from '@om/redux/selectors/publicWeb/sharePrice';
import {
    requestIndicators,
    setCommoditiesToggle,
    setCurrenciesToggle,
    setLocalToggle,
    setOffshoreToggle
} from '@om/redux/actions/publicWeb/sharePrice';
import { OmMarketIndicators } from '@om/component-library-react';
import OmButton from './forms/OmButton';

function formatDateTime(date) {
    let newDate = new Date(date)
    return newDate.toLocaleDateString('en-ZA') + ' ' + newDate.toLocaleTimeString();
}

function determineIcon(amount) {
    let number = parseFloat(amount);
    if (number === 0) {
        return 'remove';
    }
    if (number > 0) {
        return 'arrow_drop_up'
    }
    if (number < 0) {
        return 'arrow_drop_down'
    }
}

function determinePercentageChangeStyleAndIcon(amount) {
    let number = parseFloat(amount);
    if (number === 0) {
        return '<span>' + amount + '</span></om-icon>'
    }
    if (number > 0) {
        return '<span class="market-positive-green">+' + amount + '</span><om-icon id="green" icon-name=' + determineIcon(amount) + '></om-icon>'
    }
    if (number < 0) {
        return '<span class="market-negative-red">' + amount + '</span><om-icon id="red" icon-name=' + determineIcon(amount) + '></om-icon>'
    }
}

function fillIndicatorArrayObjects(indicatorArray, constrainedArraySize) {
    let desktop_array = [];
    let mobile_array = [];
    for (let i = 0; i < indicatorArray.length; i++) {
        desktop_array.push({
            row_cells: [
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: '<b>' + indicatorArray[i].name + '</b>'
                            }
                        }
                    ]
                },
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: formatDateTime(indicatorArray[i].asat)
                            }
                        }
                    ]
                },
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: indicatorArray[i].spot
                            }
                        }
                    ]
                },
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: determinePercentageChangeStyleAndIcon(indicatorArray[i].percentchange)
                            }
                        }
                    ]
                }
            ]
        });
        mobile_array.push({
            mobile_table_header_rows: [
                {
                    row_cells: [
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: indicatorArray[i].name
                                    }
                                }
                            ]
                        }
                    ]
                }
            ],
            mobile_table_body_rows: [
                {
                    row_cells: [
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: 'As At'
                                    }
                                }
                            ]
                        },
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: formatDateTime(indicatorArray[i].asat)
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    row_cells: [
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: 'Spot'
                                    }
                                }
                            ]
                        },
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: indicatorArray[i].spot
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    row_cells: [
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: '% Change'
                                    }
                                }
                            ]
                        },
                        {
                            modular_blocks: [
                                {
                                    table_cell_rich_text: {
                                        rich_text: determinePercentageChangeStyleAndIcon(indicatorArray[i].percentchange)
                                    }
                                }
                            ]
                        }
                    ]
                },
            ]
        })
    }
    return {
        desktop_table_body_rows_object_array: desktop_array,
        desktop_table_body_rows_object_array_constrained: desktop_array.slice(0, constrainedArraySize),
        mobile_tables_object_array: mobile_array,
        mobile_tables_object_array_constrained: mobile_array.slice(0, constrainedArraySize),
    }
}

function buttonText(i) {
    return 'Show ' + (i ? 'Less' : 'More') + ' Stocks'
}

function iconType(i) {
    return i ? 'arrow_upward' : 'arrow_downward';
}

const MarketIndicators = ({
    slot,
}) => {
    // Mounted hook
    let dispatch = useDispatch();
    useEffect(() => {
        dispatch(requestIndicators());
    }, [dispatch]);

    const {
        indicators,
        commoditiesExpanded,
        currenciesExpanded,
        offshoreExpanded,
        localExpanded,
    } = useSelector(
        createStructuredSelector({
            indicators: getIndicators,
            commoditiesExpanded: getCommoditiesToggle,
            currenciesExpanded: getCurrenciesToggle,
            offshoreExpanded: getOffshoreToggle,
            localExpanded: getLocalToggle,
        })
    );

    function expandLocal() {
        dispatch(setLocalToggle(!localExpanded));
    }

    function expandOffshore() {
        dispatch(setOffshoreToggle(!offshoreExpanded));
    }

    function expandCommodities() {
        dispatch(setCommoditiesToggle(!commoditiesExpanded));
    }

    function expandCurrencies() {
        dispatch(setCurrenciesToggle(!currenciesExpanded));
    }

    let constrainedArraySize = 3;


    let tableLocalDataFull = null;
    let tableLocalDataConstrained = null;
    let tableOffshoreDataFull = null;
    let tableOffshoreDataConstrained = null;
    let tableCommoditiesDataFull = null;
    let tableCommoditiesDataConstrained = null;
    let tableCurrenciesDataFull = null;
    let tableCurrenciesDataConstrained = null;

    let localJSE = indicators.indicators ? indicators.indicators.localJSE : null;
    let offshore = indicators.indicators ? indicators.indicators.offshore : null;
    let commodities = indicators.indicators ? indicators.indicators.commodities : null;
    let currencies = indicators.indicators ? indicators.indicators.currencies : null;
    // not used but is available
    // let foreign = indicators.indicators ? indicators.indicators.foreign : null;

    let disclaimer = '<p>The Dow Jones Industrial quotes the closing price of the previous day, while the MSCI Emerging Markets index is also only updated once a day. The Repo rate is adjusted following Monetary Policy Committee meetings.</p>\n' +
        '<p></p>\n' +
        '<p>Market information is supplied by ProfileData and is delayed by at least 15 minutes. <a href="https://www.profile.co.za/">Terms and conditions</a></p>\n' +
        '<p></p>\n' +
        '<p>Dow Jones indexes: © Copyright 2021 Dow Jones & Company, Inc. <a href="https://www.dowjones.com/">Terms and Conditions</a></p>';

    let desktop_table_header_row = [
        {
            row_cells: [
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: 'Local'
                            }
                        }
                    ]
                },
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: 'As At'
                            }
                        }
                    ]
                },
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: 'Spot'
                            }
                        }
                    ]
                },
                {
                    modular_blocks: [
                        {
                            table_cell_rich_text: {
                                rich_text: '% Change'
                            }
                        }
                    ]
                }
            ]
        }
    ]

    if (indicators.indicators) {
        let filledLocalJSEObject = fillIndicatorArrayObjects(localJSE, constrainedArraySize);
        tableLocalDataFull = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Local (JSE)</h1>',
            renderKey: 'local' + localExpanded,
            caption: `What's the Johannesburg Stock Exchange been up, or down to?`,
            disclaimer: '',
            table_style: 'generic',
            desktop_table_header_row: desktop_table_header_row,
            desktop_table_body_rows: filledLocalJSEObject.desktop_table_body_rows_object_array,
            mobile_tables: filledLocalJSEObject.mobile_tables_object_array
        }
        tableLocalDataConstrained = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Local (JSE)</h1>',
            caption: `What's the Johannesburg Stock Exchange been up, or down to?`,
            disclaimer: '',
            renderKey: 'local' + localExpanded,
            isExpanded: localExpanded,
            bottomButton: 'LOCAL',
            table_style: 'generic',
            desktop_table_header_row: desktop_table_header_row,
            desktop_table_body_rows: filledLocalJSEObject.desktop_table_body_rows_object_array_constrained,
            mobile_tables: filledLocalJSEObject.mobile_tables_object_array_constrained
        }

        let filledOffshoreObject = fillIndicatorArrayObjects(offshore, constrainedArraySize);
        const offshore_header_row = JSON.parse(JSON.stringify(desktop_table_header_row));
        offshore_header_row[0].row_cells[0].modular_blocks[0].table_cell_rich_text.rich_text = 'Offshore';
        tableOffshoreDataFull = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Offshore</h1>',
            caption: `Stay up to date with global market movements`,
            disclaimer: '',
            renderKey: 'offshore' + offshoreExpanded,
            bottomButton: 'OFFSHORE',
            table_style: 'generic',
            desktop_table_header_row: offshore_header_row,
            desktop_table_body_rows: filledOffshoreObject.desktop_table_body_rows_object_array,
            mobile_tables: filledOffshoreObject.mobile_tables_object_array
        }

        tableOffshoreDataConstrained = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Offshore</h1>',
            caption: `Stay up to date with global market movements`,
            disclaimer: '',
            renderKey: 'offshore' + offshoreExpanded,
            bottomButton: 'OFFSHORE',
            table_style: 'generic',
            desktop_table_header_row: offshore_header_row,
            desktop_table_body_rows: filledOffshoreObject.desktop_table_body_rows_object_array_constrained,
            mobile_tables: filledOffshoreObject.mobile_tables_object_array_constrained
        }

        let filledCommoditiesObject = fillIndicatorArrayObjects(commodities, constrainedArraySize);
        let commodities_header_row = JSON.parse(JSON.stringify(desktop_table_header_row));
        commodities_header_row[0].row_cells[0].modular_blocks[0].table_cell_rich_text.rich_text = 'Commodities';
        tableCommoditiesDataFull = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Commodities</h1>',
            caption: `Get the latest prices on commodities and trends`,
            disclaimer: '',
            renderKey: 'commodities' + commoditiesExpanded,
            bottomButton: 'COMMODITIES',
            table_style: 'generic',
            desktop_table_header_row: commodities_header_row,
            desktop_table_body_rows: filledCommoditiesObject.desktop_table_body_rows_object_array,
            mobile_tables: filledCommoditiesObject.mobile_tables_object_array
        }
        tableCommoditiesDataConstrained = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Commodities</h1>',
            caption: `Get the latest prices on commodities and trends`,
            disclaimer: '',
            renderKey: 'commodities' + commoditiesExpanded,
            bottomButton: 'COMMODITIES',
            table_style: 'generic',
            desktop_table_header_row: commodities_header_row,
            desktop_table_body_rows: filledCommoditiesObject.desktop_table_body_rows_object_array_constrained,
            mobile_tables: filledCommoditiesObject.mobile_tables_object_array_constrained
        }

        let filledCurrenciesObjects = fillIndicatorArrayObjects(currencies, constrainedArraySize);
        let currencies_header_row = JSON.parse(JSON.stringify(desktop_table_header_row));
        currencies_header_row[0].row_cells[0].modular_blocks[0].table_cell_rich_text.rich_text = 'Currencies';
        tableCurrenciesDataFull = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Currencies</h1>',
            caption: `Get the latest foreign exchange rate`,
            disclaimer: disclaimer,
            renderKey: 'currencies' + currenciesExpanded,
            bottomButton: 'CURRENCIES',
            table_style: 'generic',
            desktop_table_header_row: currencies_header_row,
            desktop_table_body_rows: filledCurrenciesObjects.desktop_table_body_rows_object_array,
            mobile_tables: filledCurrenciesObjects.mobile_tables_object_array
        }
        tableCurrenciesDataConstrained = {
            has_boundary: true,
            use_heading_as_h1: true,
            heading: '<h1>Currencies</h1>',
            caption: `Get the latest foreign exchange rate`,
            disclaimer: disclaimer,
            renderKey: 'currencies' + currenciesExpanded,
            bottomButton: 'CURRENCIES',
            table_style: 'generic',
            desktop_table_header_row: currencies_header_row,
            desktop_table_body_rows: filledCurrenciesObjects.desktop_table_body_rows_object_array_constrained,
            mobile_tables: filledCurrenciesObjects.mobile_tables_object_array_constrained
        }
    }

    return (
        <OmMarketIndicators slot={slot}>
            <div slot="market-indicators">
                <Table
                    tableContent={localExpanded ? tableLocalDataFull : tableLocalDataConstrained}
                    bottomButton={<OmButton
                        type='text'
                        size='large'
                        iconName={iconType(localExpanded)}
                        text={buttonText(localExpanded)}
                        onClick={expandLocal}/>}
                />

                <Table
                    tableContent={offshoreExpanded ? tableOffshoreDataFull : tableOffshoreDataConstrained}
                    bottomButton={<OmButton
                        type='text'
                        size='large'
                        iconName={iconType(offshoreExpanded)}
                        text={buttonText(offshoreExpanded)}
                        onClick={expandOffshore}/>}
                />

                <Table
                    tableContent={commoditiesExpanded ? tableCommoditiesDataFull : tableCommoditiesDataConstrained}
                    bottomButton={<OmButton
                        type='text'
                        size='large'
                        iconName={iconType(commoditiesExpanded)}
                        text={buttonText(commoditiesExpanded)}
                        onClick={expandCommodities}/>}
                />

                <Table
                    tableContent={currenciesExpanded ? tableCurrenciesDataFull : tableCurrenciesDataConstrained}
                    bottomButton={<OmButton
                        type='text'
                        size='large'
                        iconName={iconType(currenciesExpanded)}
                        text={buttonText(currenciesExpanded)}
                        onClick={expandCurrencies}/>}/>
            </div>
        </OmMarketIndicators>
    );
}

export default MarketIndicators;
